import axios from 'axios';
import { env } from '../env';

const STORAGE_KEY = 'django_api_key';
const EMAIL_STORAGE_KEY = 'user_email';

export const djangoClient = axios.create({
  baseURL: env.NEXT_PUBLIC_DJANGO_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: Attach API key from sessionStorage
djangoClient.interceptors.request.use(
  (config) => {
    if (typeof window !== 'undefined') {
      const apiKey = sessionStorage.getItem(STORAGE_KEY);
      const email = sessionStorage.getItem(EMAIL_STORAGE_KEY);

      if (apiKey) {
        // TastyPie endpoints (like /api/v1/*) require format: "ApiKey {username}:{api_key}"
        // Other endpoints use: "ApiKey {api_key}"
        if (config.url?.includes('/api/v1/')) {
          if (email) {
            config.headers.Authorization = `ApiKey ${email}:${apiKey}`;
            console.log('[Django Client] Using TastyPie auth format for:', config.url);
          } else {
            console.warn('[Django Client] No email found for TastyPie endpoint:', config.url);
          }
        } else {
          config.headers.Authorization = `ApiKey ${apiKey}`;
        }
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor: Handle 401/403 errors
djangoClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      typeof window !== 'undefined' &&
      error.response &&
      (error.response.status === 401 || error.response.status === 403)
    ) {
      console.error('[Django Client] Auth error:', {
        status: error.response.status,
        url: error.config?.url,
        message: error.response.data
      });

      // Clear invalid API key
      sessionStorage.removeItem(STORAGE_KEY);

      // Redirect to sign-in page if not already there
      if (window.location.pathname !== '/signin') {
        console.log('[Django Client] Redirecting to sign-in due to auth error');
        window.location.href = '/signin';
      }
    }
    return Promise.reject(error);
  }
);

// Helper functions for API key management
export const setApiKey = (apiKey: string) => {
  if (typeof window !== 'undefined') {
    sessionStorage.setItem(STORAGE_KEY, apiKey);
  }
};

export const getApiKey = (): string | null => {
  if (typeof window !== 'undefined') {
    return sessionStorage.getItem(STORAGE_KEY);
  }
  return null;
};

export const clearApiKey = () => {
  if (typeof window !== 'undefined') {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(EMAIL_STORAGE_KEY); // Also clear email
  }
};

// Helper functions for user email management
export const setUserEmail = (email: string) => {
  if (typeof window !== 'undefined') {
    sessionStorage.setItem(EMAIL_STORAGE_KEY, email);
  }
};

export const getUserEmail = (): string | null => {
  if (typeof window !== 'undefined') {
    return sessionStorage.getItem(EMAIL_STORAGE_KEY);
  }
  return null;
};
